import { clampCutawayShift, TimelineMap } from '@reels/shared';
/**
 * Демо-бэкенд для разработки без Telegram и без API.
 *
 * Включается автоматически, когда приложение открыто в обычном браузере
 * (нет initData) и не задан VITE_API_URL. Отвечает на те же пути, что и
 * настоящий сервер, поэтому остальной код о нём не знает.
 */
import { REEL_ADDON, REEL_PACKAGES, type BillingSummary } from '@reels/shared/billing';
import { DEFAULT_SETTINGS, PROJECT_TTL_DAYS } from '@reels/shared/constants';
import type { Edl, ProjectSettings, ProjectStatus, Scene } from '@reels/shared';
import { ApiError } from './api';
import { t } from '../i18n';
import type { Transport, TransportRequest } from './api';
import type { ProjectDetail, ProjectSummary } from './types';

interface DemoProject extends ProjectDetail {
  startedAt: number | null;
  /** Когда запускали генерацию: для счётчика в профиле. */
  generatedAt: number | null;
}

function demoScenes(): Scene[] {
  return [
  {
    id: 'a1:0',
    assetId: 'a1',
    start: 0,
    end: 3.2,
    description: t('mock.scene.laptop'),
    tags: [t('mock.tag.chart'), t('mock.tag.laptop')],
    usability: 0.9,
    hasFaces: false,
    hasText: true,
  },
  {
    id: 'a1:1',
    assetId: 'a1',
    start: 3.2,
    end: 7.4,
    description: t('mock.scene.team'),
    tags: [t('mock.tag.team'), t('mock.tag.office')],
    usability: 0.72,
    hasFaces: true,
    hasText: false,
  },
  {
    id: 'a2:0',
    assetId: 'a2',
    start: 0,
    end: 5,
    description: t('mock.scene.dashboard'),
    tags: [t('mock.tag.dashboard'), t('mock.tag.metrics')],
    usability: 0.81,
    hasFaces: false,
    hasText: true,
  },
  ];
}

function demoEdl(settings: ProjectSettings): Edl {
  return {
    version: 1,
    target_duration_s: 58,
    timeline: [
      { src_in: 4.2, src_out: 21.8, reason: t('mock.reason.thesis') },
      { src_in: 46.1, src_out: 72.4, reason: t('mock.reason.example') },
      { src_in: 120.0, src_out: 134.2, reason: t('mock.reason.outro') },
    ],
    hook: {
      enabled: settings.hook,
      text: t('mock.hook'),
      duration_s: 2.2,
      moved_segment: null,
    },
    cutaways: [
      {
        asset_id: 'a1',
        mode: 'full',
        tl_in: 6.4,
        tl_out: 9.8,
        src_in: 0,
        src_out: 3.4,
        motion: 'none',
        why: t('mock.why.chart'),
      },
      {
        asset_id: 'a2',
        mode: 'full',
        tl_in: 24.1,
        tl_out: 27.6,
        src_in: 0,
        src_out: 3.5,
        motion: 'kenburns',
        why: t('mock.why.dashboard'),
      },
    ],
    subtitles: {
      enabled: settings.subtitles !== 'off',
      preset: settings.subtitles === 'off' ? 'karaoke' : settings.subtitles,
      corrections: [],
    },
    music: {
      enabled: settings.music !== 'off',
      mood: settings.music === 'off' || settings.music === 'auto' ? 'upbeat' : settings.music,
      energy: 0.6,
    },
    cover: { frame_tl_s: 1.4, title: t('mock.cover') },
    publish: {
      caption: t('mock.caption'),
      hashtags: [t('mock.hashtag.marketing'), t('mock.hashtag.conversion'), t('mock.hashtag.landing'), t('mock.hashtag.sales'), t('mock.hashtag.analytics')],
    },
  };
}

const STAGE_ORDER: ProjectStatus[] = [
  'uploading',
  'transcribing',
  'analyzing',
  'planning',
  'rendering',
  'delivering',
  'done',
];

const STAGE_MS = 2500;

const projects = new Map<string, DemoProject>();
let counter = 0;

function newId(prefix: string): string {
  counter += 1;
  return `${prefix}-${counter}-${Math.random().toString(36).slice(2, 8)}`;
}

function advance(project: DemoProject): DemoProject {
  if (project.startedAt === null) return project;
  const elapsed = Date.now() - project.startedAt;
  const index = Math.min(Math.floor(elapsed / STAGE_MS), STAGE_ORDER.length - 1);
  const status = STAGE_ORDER[index] ?? 'done';
  project.status = status;
  project.stage = status;
  project.progress = Math.min(100, Math.round(elapsed / (STAGE_MS * (STAGE_ORDER.length - 1)) * 100));
  if (status === 'done') {
    project.progress = 100;
    if (!project.edl) {
      project.edl = demoEdl(project.settings);
      const sources = project.assets.filter(a => a.kind === 'reaction').sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      if (sources.length) {
        project.edl.version = 2;
        project.edl.sources = sources.map(a => ({ asset_id: a.id, filename: a.filename ?? t('mock.source'), duration_s: a.durationS ?? 1, words: [] }));
        project.edl.timeline = sources.map(a => ({ asset_id: a.id, src_in: 0, src_out: a.durationS ?? 1, reason: t('mock.reason.full') }));
        project.edl.cutaways = [];
        project.edl.target_duration_s = sources.reduce((sum, a) => sum + (a.durationS ?? 1), 0);
        project.edl.cover.frame_tl_s = Math.min(1, project.edl.target_duration_s / 2);
      }
    }
    project.edlVersion = project.edlVersion ?? 1;
    project.renderedEdlVersion = project.edlVersion;
    // Как на сервере: первый ролик пробный и со знаком, пересборка оплачена и без него.
    project.correctionCost = 1;
    project.watermarked = !project.render;
    // Как разбор на сервере: у каждой иллюстрации появляются сцены.
    project.assets = project.assets.map(a => a.kind === 'reaction' || a.scenes?.length ? a : { ...a, scenes: [{
      id: `${a.id}:0`, assetId: a.id, start: 0, end: 3, description: a.filename ?? t('mock.illustration'), tags: [], usability: 0.8, hasFaces: false, hasText: false,
    }] });
    project.candidates = demoScenes();
    project.render = {
      id: newId('render'),
      status: 'done',
      downloadUrl: null,
      coverUrl: null,
      thumbUrl: null,
      durationS: project.edl.timeline.reduce((total, segment) => total + segment.src_out - segment.src_in, 0),
      bytes: 21_400_000,
      tgFileId: null,
      music: { status: project.edl.music.enabled ? 'unavailable' : 'disabled' },
    };
    project.startedAt = null;
  }
  return project;
}

function summary(project: DemoProject): ProjectSummary {
  return {
    id: project.id,
    format: project.format,
    status: project.status,
    createdAt: project.createdAt,
    expiresAt: project.expiresAt,
    title: project.title ?? project.edl?.cover.title ?? project.assets[0]?.filename?.replace(/\.[^.]+$/, '') ?? t('mock.new_reel'),
    thumbUrl: project.render?.thumbUrl ?? project.assets[0]?.thumbUrl ?? null,
    durationS: project.render?.durationS ?? null,
  };
}

function findProject(path: string): DemoProject {
  const id = path.split('/')[2] ?? '';
  const project = projects.get(id);
  if (!project) throw new ApiError('not_found');
  return advance(project);
}

function demoBilling(): BillingSummary {
  // Explicit fixture for checking the exhausted screen without a real purchase/render.
  const params = new URLSearchParams(window.location.search);
  const tribute = params.get('checkout') === 'tribute';
  const stars = params.get('checkout') === 'stars';
  const trial = params.get('trial') === 'used' ? 0 : 1;
  const activePackage = REEL_PACKAGES.find(pack => pack.id === params.get('subscription'));
  const subscription: BillingSummary['subscription'] = activePackage ? {
    id: 'demo-subscription', provider: stars ? 'stars' : 'tribute', packageId: activePackage.id,
    periodEnd: new Date(Date.now() + 30 * 86400000).toISOString(), cancelled: params.get('cancelled') === 'true',
  } : null;
  const current = params.get('videos') === '0' ? 0 : activePackage?.videos ?? 0;
  return { enabled: true, available: trial + current, reserved: 0,
    subscription, balances:{carried:0,current,addon:0,frozen:0,trial,legacy:0,referral:0,promo:0,debt:0},
    addon:{...REEL_ADDON,available:!!subscription && (stars || tribute),starsAvailable:stars && !!subscription,tributeAvailable:tribute && !!subscription},
    packages: REEL_PACKAGES.map(pack => ({ ...pack, tribute })), starsEnabled: stars, tributeEnabled: tribute, history: [] };
}

export const demoTransport: Transport = async (request: TransportRequest): Promise<unknown> => {
  // Небольшая задержка, чтобы состояния загрузки были видны.
  await new Promise((resolve) => window.setTimeout(resolve, 180));
  const { method, path } = request;

  if (method === 'POST' && path === '/auth/telegram') {
    return {
      token: 'demo-token',
      user: { id: 'demo-user', tgId: 1, lang: 'ru', name: t('mock.user'), onboardingSeen: false },
    };
  }

  if (method === 'GET' && path === '/billing') return demoBilling();
  if (method === 'POST' && path === '/promo/redeem') {
    const billing = demoBilling();
    return { videos: 5, billing: { ...billing, available: billing.available + 5, balances: { ...billing.balances, promo: 5 } } };
  }
  if (method === 'PATCH' && path === '/me') return { ok: true };

  if (method === 'GET' && path === '/me') {
    const all = [...projects.values()].map(advance);
    const dayAgo = Date.now() - 24 * 3600 * 1000;
    // Ссылки в демо ведут на публичные страницы Telegram: настоящие задаются в .env API.
    return {
      user: { id: 'demo-user', tgId: 1, username: 'demo', lang: 'ru', createdAt: new Date().toISOString() },
      billing: demoBilling(),
      stats: {
        rendered: all.filter((project) => project.render !== null).length,
        generationsToday: all.filter(
          (project) => project.generatedAt !== null && project.generatedAt > dayAgo,
        ).length,
      },
      links: {
        support: 'https://t.me/telegram',
        channel: 'https://t.me/telegram',
        terms: 'https://telegram.org/tos',
        privacy: 'https://telegram.org/privacy',
      },
      // Несколько приглашённых, чтобы в демо был виден график за неделю.
      referral: {
        code: 'ref_1',
        link: 'https://t.me/reelsmakerapp_bot?start=ref_1',
        invited: 3,
        currency: 'XTR',
        earnedQuarterStars: 1170, availableQuarterStars: 1170, debtQuarterStars: 0,
        reservedQuarterStars: 0, paidQuarterStars: 0, minimumWithdrawalStars: 1000, withdrawals: [],
        invitedByDay: [0, 1, 0, 0, 2, 0, 0],
      },
    };
  }

  if (method === 'GET' && path === '/projects') {
    return { items: [...projects.values()].map((project) => summary(advance(project))) };
  }

  if (method === 'POST' && path === '/projects') {
    const id = newId('demo');
    const now = new Date();
    const project: DemoProject = {
      id,
      format: 'reaction',
      status: 'draft',
      createdAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + PROJECT_TTL_DAYS * 24 * 3600 * 1000).toISOString(),
      settings: { ...DEFAULT_SETTINGS },
      assets: [],
      edl: null,
      edlVersion: null,
      candidates: [],
      render: null,
      stage: null,
      progress: null,
      error: null,
      startedAt: null,
      generatedAt: null,
    };
    projects.set(id, project);
    return project;
  }

  if (path.startsWith('/projects/')) {
    const project = findProject(path);
    const tail = path.split('/').slice(3).join('/');

    if (method === 'GET' && tail === '') return project;
    if (method === 'PATCH' && tail === '') {
      project.title = (request.body as { title: string }).title.trim();
      return project;
    }
    if (method === 'DELETE' && tail === '') {
      projects.delete(project.id);
      return { ok: true };
    }
    if (method === 'PATCH' && tail === 'settings') {
      project.settings = request.body as ProjectSettings;
      return project;
    }
    if (method === 'PUT' && tail === 'sources/order') {
      const { assetIds } = request.body as { assetIds: string[] };
      project.assets = project.assets.map(a => ({ ...a, position: assetIds.includes(a.id) ? assetIds.indexOf(a.id) : a.position }));
      return { ok: true };
    }
    if (method === 'POST' && tail === 'generate') {
      project.startedAt = Date.now();
      project.generatedAt = Date.now();
      project.status = 'uploading';
      project.error = null;
      return { ok: true };
    }
    if (method === 'POST' && tail === 'remontage') {
      project.startedAt = Date.now();
      project.status = 'transcribing';
      project.error = null;
      return { ok: true };
    }
    if (method === 'POST' && tail === 'rerender') {
      if ((request.body as { expectedVersion?: number })?.expectedVersion !== project.edlVersion) throw new ApiError('project_busy', t('mock.edit_changed'), 409);
      project.startedAt = Date.now() - STAGE_MS * 4;
      project.status = 'rendering';
      return { ok: true };
    }
    if (method === 'POST' && tail === 'deliver') return { ok: true };
    if (method === 'PATCH' && tail === 'edl') {
      const patch = request.body as Record<string, unknown>;
      if (patch['expectedVersion'] !== project.edlVersion) throw new ApiError('project_busy', t('mock.edit_changed'), 409);
      const edl = project.edl ?? demoEdl(project.settings);
      const hook = patch['hook'] as { text?: string; enabled?: boolean } | undefined;
      if (hook?.text !== undefined) edl.hook.text = hook.text;
      if (hook?.enabled !== undefined) edl.hook.enabled = hook.enabled;
      const cover = patch['cover'] as { title?: string } | undefined;
      if (cover?.title !== undefined) edl.cover.title = cover.title;
      const effects = patch['effects'] as Partial<Pick<ProjectSettings, 'hookStyle' | 'subtitleSound' | 'subtitlePosition' | 'hookPosition' | 'subtitleDepth' | 'accentZooms' | 'colorFilter' | 'colorIntensity' | 'highlight' | 'transition' | 'transitionDuration' | 'noiseReduction' | 'musicVolume'>> | undefined;
      if (effects) project.settings = { ...project.settings, ...effects };
      const subtitles = patch['subtitles'] as { preset?: Edl['subtitles']['preset']; enabled?: boolean } | undefined;
      if (subtitles?.preset) edl.subtitles.preset = subtitles.preset;
      if (subtitles?.enabled !== undefined) edl.subtitles.enabled = subtitles.enabled;
      project.settings.subtitles = edl.subtitles.enabled ? edl.subtitles.preset : 'off';
      const music = patch['music'] as { enabled?: boolean } | undefined;
      if (typeof music?.enabled === 'boolean') edl.music.enabled = music.enabled;
      const corrections = patch['corrections'] as Array<{ from: string; to: string }> | undefined;
      if (corrections) edl.subtitles.corrections = corrections;
      const publish = patch['publish'] as { caption?: string; hashtags?: string[] } | undefined;
      if (publish?.caption !== undefined) edl.publish.caption = publish.caption;
      if (publish?.hashtags !== undefined) edl.publish.hashtags = publish.hashtags;
      const cutaways = patch['cutaways'] as
        | Array<{ index: number; op: 'replace' | 'shift' | 'remove'; deltaS?: number; sceneId?: string }>
        | undefined;
      if (cutaways) {
        for (const op of cutaways) {
          const target = edl.cutaways[op.index];
          if (!target) continue;
          if (op.op === 'remove') continue;
          else if (op.op === 'shift' && typeof op.deltaS === 'number') {
            const delta = clampCutawayShift(target, op.deltaS, new TimelineMap(edl.timeline).totalDuration);
            target.tl_in += delta;
            target.tl_out += delta;
          } else if (op.op === 'replace' && op.sceneId) {
            const scene = demoScenes().find((item) => item.id === op.sceneId);
            if (scene) {
              target.asset_id = scene.assetId;
              target.src_in = scene.start;
              target.src_out = scene.start + Math.min(scene.end - scene.start, target.tl_out - target.tl_in);
              target.tl_out = target.tl_in + target.src_out - target.src_in;
              target.why = scene.description;
            }
          }
        }
      }
      if (cutaways) edl.cutaways = edl.cutaways.filter((_, index) => !cutaways.some(op => op.op === 'remove' && op.index === index));
      project.edl = edl;
      project.edlVersion = (project.edlVersion ?? 1) + 1;
      return { version: project.edlVersion, edl, settings: project.settings };
    }
    if (method === 'POST' && tail === 'assets') {
      const assetId = newId('asset');
      const input = request.body as import('./types').CreateAssetRequest;
      project.assets.push({ id: assetId, ...input, uploaded: false });
      return {
        assetId,
        uploadId: newId('upload'),
        key: `uploads/demo/${assetId}`,
        partSizeBytes: 12 * 1024 * 1024,
        parts: [{ partNumber: 1, url: 'about:blank' }],
      };
    }
  }

  if (path.startsWith('/assets/')) {
    const assetId = path.split('/')[2] ?? '';
    const project = [...projects.values()].find(p => p.assets.some(a => a.id === assetId));
    const asset = project?.assets.find(a => a.id === assetId);
    if (method === 'POST' && asset) { asset.uploaded = true; return { ok: true, asset }; }
    if (method === 'DELETE') { if (project) project.assets = project.assets.filter(a => a.id !== assetId); return { ok: true }; }
  }

  throw new ApiError('not_found', `Демо-режим не знает путь ${method} ${path}`);
};
